import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db, storage } from './../../../firebase.js'; 
import { collection, query, doc, getCountFromServer, getDocs, limit, orderBy, onSnapshot, where, collectionGroup } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import ReactECharts from 'echarts-for-react';
import './AdminPage.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getCategoryColor, getCategoryLabelColor, hasConfiguredCategoryColor } from './../../../common/categoryColors.js';
import { getAdminEvalDisplayLabel } from './../../../common/adminEvalLabel.js';
import { getAppRecordImagePath } from './../../../common/appRecordImages.js';
import { ADMIN_PANEL_COLUMNS, layoutAdminPanelCards } from './../../../common/adminPanelLayout.js';

// Temporary chart testing toggle:
// Set to false to switch back to realtime Firestore data.
const USE_MOCK_INFERENCE_TREND_DATA = false;

const ADMIN_CONSTITUENT_LABEL_MAX = 8;

// Dangerous-waste samples captured through the mobile app are stored per app
// owner instead of per unit, so no usage-by-day trigger counts them. The
// "Mobile" row reads company_app_records/{companyId}/dangerous-waste-records
// directly and sits directly below the dangerous-waste row.
const DANGEROUS_WASTE_CATEGORY = "dangerous_waste";
const COMPANY_APP_RECORDS_COLLECTION = "company_app_records";
const APP_RECORDS_SUBCOLLECTION = "dangerous-waste-records";
const MOBILE_ROW_TITLE = "Mobile";
const MOBILE_UNIT_ID = "mobile";

// One tile in the panel grid is the size these units' cards are drawn at: their cameras
// produce the shape every other card is cropped to fit.
const PANEL_TILE_REFERENCE_CATEGORIES = ["wood", "mineral_wool"];

// Used until one of those unit images has loaded and reported its own shape.
const PANEL_TILE_FALLBACK_ASPECT_RATIO = 4 / 3;

// A dangerous-waste card is one tile wide and two tall, so its scan gets the room its detail
// needs. Everything else, the mobile app scans included, is a single tile.
const DANGEROUS_WASTE_CARD_ROW_SPAN = 2;

// Units without a deployment_config category fall back to this bucket. 
const UNCATEGORIZED_CATEGORY = "Uncategorized";

// units/mobile carries no deployment_config, so the mobile app has no system
// version, model or memory reading to report. These stand in until the app
// starts publishing them.
const MOBILE_UNIT_DEFAULTS = {
    unitId: MOBILE_UNIT_ID,
    systemVersion: "1.0.1",
    model: "dangerous_waste",
    memoryUse: null
};

// Compact display of all-time counts, e.g. 24350 -> "24.35 k".
function formatAllTimeCount(count) {
    const value = Number(count);
    if (!Number.isFinite(value) || value <= 0) return "0";
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)} M`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)} k`;
    return String(value);
}

// A company is a test company when any of the spellings the flag has been written under says
// so. Only an explicit true counts: a missing flag is a real company.
function isTestCompanyFlag(companyData) {
    return [companyData?.test_company, companyData?.testCompany, companyData?.is_test_company]
        .some((flag) => flag === true || flag === "true");
}

// Single-camera dangerous-waste units put their scan in back_images; multi-camera units leave
// that empty and name one image per camera, of which the top-left one is shown.
function getDangerousWasteRecordImageName(recordData) {
    const backImages = Array.isArray(recordData.back_images) ? recordData.back_images : [];
    const latestBackImage = backImages[backImages.length - 1];
    if (typeof latestBackImage === "string") return latestBackImage;
    if (latestBackImage && typeof latestBackImage === "object") {
        const backImageName = latestBackImage.img_name || latestBackImage.name;
        if (backImageName) return backImageName;
    }

    return recordData.images_by_camera?.top_left
        || recordData.cameras?.top_left?.image
        || null;
}

function truncateAdminConstituentLabel(value) {
    if (value == null || value === '') return null;
    const s = String(value).trim();
    if (!s) return null;
    if (s.length <= ADMIN_CONSTITUENT_LABEL_MAX) return s;
    return `${s.slice(0, ADMIN_CONSTITUENT_LABEL_MAX)}...`;
}

function AdminPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [recentImageUrls, setRecentImageUrls] = useState({});
    const [screenMonitoringUrls, setScreenMonitoringUrls] = useState({});
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

    const [weekUsage, setWeekUsage] = useState([])
    const [weekIsoDays, setWeekIsoDays] = useState([])
    const [usageCategories, setUsageCategories] = useState(["total"])
    const [usageDayCountsByCategory, setUsageDayCountsByCategory] = useState({})
    const [excludedRecentImageUnitIds, setExcludedRecentImageUnitIds] = useState([])
    const [excludedStatsUnitIds, setExcludedStatsUnitIds] = useState([])
    const [unitCategoryById, setUnitCategoryById] = useState({})
    const [unitNavigationById, setUnitNavigationById] = useState({})
    const [latestStateByUnit, setLatestStateByUnit] = useState({})
    const [recentInferenceRecords, setRecentInferenceRecords] = useState([])
    const [recentDangerousWasteRecords, setRecentDangerousWasteRecords] = useState([])
    const [inferenceTrendOption, setInferenceTrendOption] = useState(null)
    const [appRecordCompanies, setAppRecordCompanies] = useState([])
    const [mobileImageUrls, setMobileImageUrls] = useState({})
    const [referenceAspectRatio, setReferenceAspectRatio] = useState(null)
    const [panelGridWidth, setPanelGridWidth] = useState(null)
    const panelGridResizeObserverRef = useRef(null)
    const [mobileDayCountsByCompany, setMobileDayCountsByCompany] = useState({})
    const [mobileAllTime, setMobileAllTime] = useState(0)

    const getRecordImageUrl = async (img_name) => {
        const imgRef = ref(storage, `images/${img_name}`);
        const url = await getDownloadURL(imgRef);
        return url
    };

    const getScreenMonitoringImageUrl = async (img_name) => {
        const imgRef = ref(storage, `unit_screenshots/${img_name}`);
        const url = await getDownloadURL(imgRef);
        return url
    }

    const getShade = (baseColor, count, maxCount) => {
        if (count === 0) return '';
        const lightenFactor = (1 - count / maxCount) * 1;
        return lightenColor(baseColor, lightenFactor);
    };

    const getColorWithOpacity = (baseColor, opacity) => {
        const colorObj = hexToRgb(baseColor);
        return `rgba(${colorObj.r},${colorObj.g},${colorObj.b},${opacity})`;
    };

    // Function to lighten a color by a given factor
    const lightenColor = (color, factor) => {
        const colorObj = hexToRgb(color);
        const r = Math.round(colorObj.r + (255 - colorObj.r) * factor);
        const g = Math.round(colorObj.g + (255 - colorObj.g) * factor);
        const b = Math.round(colorObj.b + (255 - colorObj.b) * factor);
        return `rgb(${r},${g},${b})`;
    };

    // Convert hex color to RGB
    const hexToRgb = (hex) => {
        const bigint = parseInt(hex.slice(1), 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255,
        };
    };

    // Row colours come from the category config; the row position is only used
    // as a fallback for categories that have no entry there.
    const getBaseColor = (index) => getCategoryColor(weekUsage[index]?.title, index);

    const getShadeOfDayValue = (value, index) => {
        const baseColor = getBaseColor(index)
        const maxValue = Math.max(...weekUsage[index].usage)
        const opacity = maxValue > 0 ? value / maxValue : 0
        const color = opacity > 0 ? getColorWithOpacity(baseColor, Math.max(opacity, 0.1)) : 'rgb(255, 255, 255, 0.1)'

        return color
    }

    const getLastSevenIsoDays = () => {
        const now = new Date();
        const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const days = [];
        for (let offset = 6; offset >= 0; offset -= 1) {
            const day = new Date(todayUtc);
            day.setUTCDate(todayUtc.getUTCDate() - offset);
            days.push(day.toISOString().split('T')[0]);
        }
        return days;
    };

    const getIsoDayFromTimestamp = (timestampMs) => {
        const date = new Date(Number(timestampMs));
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        return utcDate.toISOString().split('T')[0];
    };

    const getCategoryLineColor = (categoryLabel) => {
        const normalized = categoryLabel === "total" ? "Total" : categoryLabel;
        const weekIndex = weekUsage.findIndex((row) => row.title === normalized);
        // Categories with no row still get their configured colour; the index
        // fallback only matters for categories missing from the config.
        return getCategoryColor(normalized, weekIndex >= 0 ? weekIndex : 1);
    };

    const getWeekdayLabelFromIsoDay = (isoDay) => {
        const date = new Date(`${isoDay}T00:00:00Z`);
        if (Number.isNaN(date.getTime())) {
            return isoDay;
        }
        return weekDays[(date.getUTCDay() + 6) % 7];
    };

    const getWeekdayLabelFromTimestamp = (timestampMs) => {
        const date = new Date(Number(timestampMs));
        if (Number.isNaN(date.getTime())) {
            return "";
        }
        return weekDays[(date.getUTCDay() + 6) % 7];
    };

    const getCategoryOverlayColor = (category) => {
        const categoryRowIndex = weekUsage.findIndex((row) => row.title === category);
        if (categoryRowIndex === -1 && !hasConfiguredCategoryColor(category)) {
            return 'rgba(0, 0, 0, 0.6)';
        }

        const baseColor = getCategoryColor(category, categoryRowIndex);
        const colorObj = hexToRgb(baseColor);
        return `rgba(${colorObj.r},${colorObj.g},${colorObj.b},0.3)`;
    };

    const getCategoryUnitTextColor = (category) => {
        const categoryRowIndex = weekUsage.findIndex((row) => row.title === category);
        if (categoryRowIndex === -1 && !hasConfiguredCategoryColor(category)) {
            return 'rgba(0, 0, 0, 0.85)';
        }

        const configuredLabelColor = getCategoryLabelColor(category);
        if (configuredLabelColor) {
            return configuredLabelColor;
        }

        const baseColor = getCategoryColor(category, categoryRowIndex);
        const colorObj = hexToRgb(baseColor);
        const darkenFactor = 0.1;
        const r = Math.round(colorObj.r * darkenFactor);
        const g = Math.round(colorObj.g * darkenFactor);
        const b = Math.round(colorObj.b * darkenFactor);
        return `rgb(${r},${g},${b})`;
    };

    const formatDateWithoutYear = (timestamp) => {
        const date = new Date(Number(timestamp));
        if (Number.isNaN(date.getTime())) {
            return "";
        }
        const locale = i18n?.language || undefined;
        const localizedDate = new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'short'
        }).format(date).replace(/\.$/, '');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${localizedDate} - ${hours}:${minutes}`;
    };

    const getStatusLedColor = (unitId) => {
        const latestState = latestStateByUnit[unitId];
        if (!latestState) {
            return 'var(--gray-500)';
        }

        const state = latestState.state;
        const timestamp = Number(latestState.timestamp);
        const oneHourAgo = Date.now() - (1 * 60 * 60 * 1000);

        if (state === "Error") {
            return 'var(--error-500)';
        }

        if (state === "Warning") {
            return 'var(--warning-500)';
        }

        if (Number.isFinite(timestamp) && timestamp >= oneHourAgo) {
            return 'var(--success-500)';
        }

        return 'var(--gray-500)';
    };

    const isRangeActive = (range, now) => {
        if (!range || !range.unit_id) return false;
        if (range.current === true) return true;
        if (range.start_date === undefined || range.start_date === null) return false;
        if (range.end_date === undefined || range.end_date === null) return false;
        return now >= range.start_date && now <= range.end_date;
    };

    const shortenModelPath = (path) => {
        const epochIndex = path.indexOf('_epoch');
        if (epochIndex === -1) return path;
        const beforeEpoch = path.substring(0, epochIndex);
        const lastUnderscore = beforeEpoch.lastIndexOf('_');
        if (lastUnderscore === -1) return beforeEpoch;
        return beforeEpoch.substring(lastUnderscore + 1);
    };

    const navigateToUnitDashboard = (unitId) => {
        const target = unitNavigationById[unitId];
        if (!target) return;
        navigate(`/locations/${target.locationId}/unit/${target.targetId}/dashboard`);
    };

    // Every tile in the panel grid is the size a wood or mineral-wool card is drawn at, and
    // every image is cropped to fill its tile. The tile height is that camera's shape applied
    // to the column width rather than a measured height, since measuring a card that is itself
    // sized by the tile would just feed itself. Read the shape off the first such image to
    // load; until one does, PANEL_TILE_FALLBACK_ASPECT_RATIO stands in.
    const captureReferenceAspectRatio = (event) => {
        if (referenceAspectRatio) return;

        const { naturalWidth, naturalHeight } = event.target;
        if (!naturalWidth || !naturalHeight) return;

        setReferenceAspectRatio(naturalWidth / naturalHeight);
    };

    // The column width comes from the grid itself, so the tiles follow a window resize.
    const measurePanelGrid = useCallback((node) => {
        if (panelGridResizeObserverRef.current) {
            panelGridResizeObserverRef.current.disconnect();
            panelGridResizeObserverRef.current = null;
        }
        if (!node || typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width;
            if (width > 0) setPanelGridWidth(width);
        });
        observer.observe(node);
        panelGridResizeObserverRef.current = observer;
    }, []);

    useEffect(() => () => {
        if (panelGridResizeObserverRef.current) {
            panelGridResizeObserverRef.current.disconnect();
            panelGridResizeObserverRef.current = null;
        }
    }, []);

    const panelTileHeight = panelGridWidth
        ? (panelGridWidth / ADMIN_PANEL_COLUMNS) / (referenceAspectRatio || PANEL_TILE_FALLBACK_ASPECT_RATIO)
        : null;

    // The company's own app-scan images page, the same one the home page links its app card to.
    const navigateToCompanyAppRecords = (companyId) => {
        navigate(`/app-records/company/${companyId}/dangerous-waste/images`);
    };

    const extractModelPathsFromUnit = (unit) => {
        const deploymentConfig = unit?.deployment_config || {};
        const output = [];

        const pushValue = (value) => {
            if (typeof value === "string" && value.trim().length > 0) {
                output.push(value.trim());
            }
        };

        const pushFromModelObject = (model) => {
            if (!model || typeof model !== "object") return;
            pushValue(model.model_path);
        };

        const classificationModels = deploymentConfig?.classification?.models;
        if (Array.isArray(classificationModels)) {
            classificationModels.forEach((model) => {
                if (typeof model === "string") {
                    return;
                } else {
                    pushFromModelObject(model);
                }
            });
        } else if (classificationModels && typeof classificationModels === "object") {
            Object.values(classificationModels).forEach((model) => {
                if (typeof model === "string") {
                    return;
                } else {
                    pushFromModelObject(model);
                }
            });
        }

        return [...new Set(output)];
    };

    useEffect(() => {
        const excludedRecentImagesUnsubscribe = onSnapshot(
            doc(db, "unit_filter_environments", "excluded_from_recent_images"),
            (excludedDoc) => {
                const excludedData = excludedDoc.exists() ? (excludedDoc.data() || {}) : {};
                const unitsArray = Array.isArray(excludedData.units) ? excludedData.units : [];
                setExcludedRecentImageUnitIds(unitsArray.map((unitId) => String(unitId)));
            },
            (error) => {
                console.log("Error listening to excluded recent images list:", error);
                setExcludedRecentImageUnitIds([]);
            }
        );

        return () => excludedRecentImagesUnsubscribe();
    }, []);

    useEffect(() => {
        const excludedStatsUnsubscribe = onSnapshot(
            doc(db, "unit_filter_environments", "excluded_from_statistics"),
            (excludedDoc) => {
                const excludedData = excludedDoc.exists() ? (excludedDoc.data() || {}) : {};
                const unitsArray = Array.isArray(excludedData.units) ? excludedData.units : [];
                setExcludedStatsUnitIds(unitsArray.map((unitId) => String(unitId)));
            },
            (error) => {
                console.log("Error listening to excluded statistics list:", error);
                setExcludedStatsUnitIds([]);
            }
        );

        return () => excludedStatsUnsubscribe();
    }, []);

    useEffect(() => {
        const locationsUnsubscribe = onSnapshot(collectionGroup(db, "locations"), (locationsSnapshot) => {
            const now = Date.now();
            const nextNavigationById = {};

            locationsSnapshot.forEach((locationDoc) => {
                const locationData = locationDoc.data() || {};
                const locationId = locationDoc.id;

                if (Array.isArray(locationData.display_units)) {
                    locationData.display_units.forEach((displayUnit, displayUnitIndex) => {
                        const unitRanges = Array.isArray(displayUnit?.unit_ranges) ? displayUnit.unit_ranges : [];
                        unitRanges.forEach((range) => {
                            if (!isRangeActive(range, now)) return;
                            const unitId = range.unit_id;
                            if (!unitId || nextNavigationById[unitId]) return;
                            nextNavigationById[unitId] = {
                                type: "display",
                                locationId,
                                targetId: displayUnitIndex
                            };
                        });
                    });
                }

                if (Array.isArray(locationData.units)) {
                    locationData.units.forEach((unitId) => {
                        if (!unitId || nextNavigationById[unitId]) return;
                        nextNavigationById[unitId] = {
                            type: "legacy",
                            locationId,
                            targetId: unitId
                        };
                    });
                }
            });

            setUnitNavigationById(nextNavigationById);
        }, (error) => {
            console.log("Error listening to locations for image navigation:", error);
        });

        return () => locationsUnsubscribe();
    }, []);

    useEffect(() => {
        let recordUnsubscribers = [];
        let stateUnsubscribers = [];

        const unitsUnsubscribe = onSnapshot(collection(db, "units"), (unitsSnapshot) => {
            recordUnsubscribers.forEach((unsubscribe) => unsubscribe());
            stateUnsubscribers.forEach((unsubscribe) => unsubscribe());
            recordUnsubscribers = [];
            stateUnsubscribers = [];

            const nextCategories = new Set(["total"]);
            const activeUnitIds = new Set();
            const nextUnitCategoryById = {};

            unitsSnapshot.forEach((unitDoc) => {
                const unit = unitDoc.data() || {};
                const unitId = unit.unit_id || unitDoc.id;
                if (!unitId) return;

                activeUnitIds.add(unitId);

                const unitCategory = (unit.deployment_config && unit.deployment_config.category) || "Uncategorized";
                nextCategories.add(unitCategory);
                nextUnitCategoryById[unitId] = unitCategory;
                const modelPaths = extractModelPathsFromUnit(unit);
                const systemVersion = unit?.system_version ? String(unit.system_version) : "-";
                const memoryUse = unit?.memory_use != null ? String(unit.memory_use) : null;

                if (unit.last_screenshot_name) {
                    getScreenMonitoringImageUrl(unit.last_screenshot_name).then((url) => {
                        setScreenMonitoringUrls((prev) => ({ ...prev, [unitId]: url }));
                    }).catch((error) => {
                        console.log("Error fetching screen monitoring image for unit:", unitId, error);
                    });
                }

                // A unit's records land in inference-records or dangerous-waste-records depending on
                // the software it runs, not on its category (a "Farligt affald prototype" unit writes
                // dangerous-waste records), so both are watched and the newer record wins — the same
                // way the usage-by-day functions count both collections for every unit.
                const latestRecordByCollection = {};

                const clearRecentImage = () => {
                    setRecentImageUrls((prev) => {
                        if (!(unitId in prev)) return prev;
                        const next = { ...prev };
                        delete next[unitId];
                        return next;
                    });
                };

                const showLatestRecord = () => {
                    const latest = Object.entries(latestRecordByCollection)
                        .filter(([, recordData]) => recordData)
                        .sort(([, a], [, b]) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0))[0];

                    if (!latest) {
                        clearRecentImage();
                        return;
                    }

                    const [recordsCollectionName, recordData] = latest;
                    const isDangerousWasteRecord = recordsCollectionName === "dangerous-waste-records";
                    const latestImageName = isDangerousWasteRecord
                        ? getDangerousWasteRecordImageName(recordData)
                        : (recordData.img_name || null);

                    if (!latestImageName) {
                        clearRecentImage();
                        return;
                    }

                    const evalLabel = getAdminEvalDisplayLabel(recordData.eval);
                    const prediction = isDangerousWasteRecord
                        ? truncateAdminConstituentLabel(evalLabel) || "???"
                        : (evalLabel ?? "???");

                    getRecordImageUrl(latestImageName).then((url) => {
                        setRecentImageUrls((prev) => ({
                            ...prev,
                            [unitId]: {
                                url,
                                prediction,
                                predictionIsRaw: isDangerousWasteRecord,
                                category: unitCategory || "Uncategorized",
                                timestamp: Number(recordData.timestamp),
                                systemVersion,
                                modelPaths,
                                memoryUse
                            }
                        }));
                    }).catch((error) => {
                        console.log("Error fetching record image for unit:", unitId, error);
                    });
                };

                ["inference-records", "dangerous-waste-records"].forEach((recordsCollectionName) => {
                    const latestRecordQuery = query(
                        collection(db, "units", unitId, recordsCollectionName),
                        orderBy("timestamp", "desc"),
                        limit(10)
                    );

                    const recordUnsubscribe = onSnapshot(latestRecordQuery, (recordSnapshot) => {
                        const firstNonTest = recordSnapshot.docs.find((d) => {
                            const data = d.data() || {};
                            return data.is_test !== true;
                        });
                        latestRecordByCollection[recordsCollectionName] = firstNonTest ? (firstNonTest.data() || {}) : null;
                        showLatestRecord();
                    }, (error) => {
                        console.log(`Error listening to latest ${recordsCollectionName} for unit:`, unitId, error);
                    });

                    recordUnsubscribers.push(recordUnsubscribe);
                });

                const latestStateQuery = query(
                    collection(db, "units", unitId, "state-logs"),
                    orderBy("timestamp", "desc"),
                    limit(1)
                );

                const stateUnsubscribe = onSnapshot(latestStateQuery, (stateSnapshot) => {
                    if (stateSnapshot.empty) {
                        setLatestStateByUnit((prev) => {
                            if (!(unitId in prev)) return prev;
                            const next = { ...prev };
                            delete next[unitId];
                            return next;
                        });
                        return;
                    }

                    const stateData = stateSnapshot.docs[0].data() || {};
                    setLatestStateByUnit((prev) => ({
                        ...prev,
                        [unitId]: {
                            state: stateData.state,
                            timestamp: Number(stateData.timestamp)
                        }
                    }));
                }, (error) => {
                    console.log("Error listening to latest state for unit:", unitId, error);
                });

                stateUnsubscribers.push(stateUnsubscribe);
            });

            setUsageCategories(Array.from(nextCategories));
            setUnitCategoryById(nextUnitCategoryById);
            setRecentImageUrls((prev) => {
                const next = {};
                Object.keys(prev).forEach((unitId) => {
                    if (activeUnitIds.has(unitId)) next[unitId] = prev[unitId];
                });
                return next;
            });
            setScreenMonitoringUrls((prev) => {
                const next = {};
                Object.keys(prev).forEach((unitId) => {
                    if (activeUnitIds.has(unitId)) next[unitId] = prev[unitId];
                });
                return next;
            });
            setLatestStateByUnit((prev) => {
                const next = {};
                Object.keys(prev).forEach((unitId) => {
                    if (activeUnitIds.has(unitId)) next[unitId] = prev[unitId];
                });
                return next;
            });
        }, (error) => {
            console.log("Error listening to units:", error);
        });

        return () => {
            recordUnsubscribers.forEach((unsubscribe) => unsubscribe());
            stateUnsubscribers.forEach((unsubscribe) => unsubscribe());
            unitsUnsubscribe();
        };
    }, []);

    useEffect(() => {
        if (USE_MOCK_INFERENCE_TREND_DATA) {
            setRecentInferenceRecords([]);
            setRecentDangerousWasteRecords([]);
            return () => {};
        }

        const sevenIsoDays = getLastSevenIsoDays();
        const startDay = new Date(`${sevenIsoDays[0]}T00:00:00Z`);
        const startMs = startDay.getTime();

        const inferenceQuery = query(
            collectionGroup(db, "inference-records"),
            where("timestamp", ">=", startMs)
        );
        const dangerousWasteQuery = query(
            collectionGroup(db, "dangerous-waste-records"),
            where("timestamp", ">=", startMs)
        );

        const inferenceUnsubscribe = onSnapshot(
            inferenceQuery,
            (snapshot) => {
                const records = snapshot.docs.map((recordDoc) => {
                    const data = recordDoc.data() || {};
                    const parentUnitDoc = recordDoc.ref.parent.parent;
                    return {
                        unitId: parentUnitDoc ? parentUnitDoc.id : null,
                        timestamp: Number(data.timestamp),
                        inferenceTime: Number(data.inference_time),
                        isTest: data.is_test === true
                    };
                }).filter((record) => record.unitId && Number.isFinite(record.timestamp) && Number.isFinite(record.inferenceTime) && !record.isTest);
                setRecentInferenceRecords(records);
            },
            (error) => {
                console.log("Error listening to inference records for trend chart:", error);
                setRecentInferenceRecords([]);
            }
        );

        const dangerousUnsubscribe = onSnapshot(
            dangerousWasteQuery,
            (snapshot) => {
                const records = snapshot.docs.map((recordDoc) => {
                    const data = recordDoc.data() || {};
                    const parentUnitDoc = recordDoc.ref.parent.parent;
                    return {
                        unitId: parentUnitDoc ? parentUnitDoc.id : null,
                        // "units" for unit records, "company_app_records" for
                        // records the mobile app wrote against an app owner.
                        ownerCollectionId: parentUnitDoc?.parent?.id || null,
                        timestamp: Number(data.timestamp),
                        inferenceTime: Number(data.inference_time),
                        isTest: data.is_test === true
                    };
                }).filter((record) => record.unitId && Number.isFinite(record.timestamp) && Number.isFinite(record.inferenceTime) && !record.isTest);
                setRecentDangerousWasteRecords(records);
            },
            (error) => {
                console.log("Error listening to dangerous-waste records for trend chart:", error);
                setRecentDangerousWasteRecords([]);
            }
        );

        return () => {
            inferenceUnsubscribe();
            dangerousUnsubscribe();
        };
    }, []);

    // App records are keyed by company, and the company_app_records parent
    // documents do not necessarily exist, so the company ids come from the
    // companies collection rather than from company_app_records itself.
    useEffect(() => {
        let cancelled = false;

        getDocs(collection(db, "companies"))
            .then((companiesSnapshot) => {
                if (cancelled) return;
                setAppRecordCompanies(companiesSnapshot.docs.map((companyDoc) => {
                    const companyData = companyDoc.data() || {};
                    return {
                        id: companyDoc.id,
                        name: companyData.name || companyData.display_name || companyData.label || companyDoc.id,
                        // Read loosely: a company flagged in an older tool may carry the string
                        // "true", or the camelCase spelling, rather than a boolean test_company.
                        isTestCompany: isTestCompanyFlag(companyData),
                        // A company saved before the active flag existed is an active one.
                        isActive: companyData.active !== false
                    };
                }));
            })
            .catch((error) => {
                console.log("Error loading companies for mobile app usage:", error);
                if (!cancelled) setAppRecordCompanies([]);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // A test company's scans are not real usage, so they are kept out of the Mobile usage row
    // and its all-time total. An inactive company's past scans are real usage that happened,
    // so they stay counted.
    const appRecordCompanyIds = useMemo(
        () => appRecordCompanies
            .filter((company) => !company.isTestCompany)
            .map((company) => company.id),
        [appRecordCompanies]
    );

    // The image panels are a live view of who is scanning now, so an inactive company drops
    // out of them even though its history still counts above.
    const mobilePanelCompanies = useMemo(
        () => appRecordCompanies.filter((company) => !company.isTestCompany && company.isActive),
        [appRecordCompanies]
    );

    const mobileImageCards = useMemo(
        () => Object.entries(mobileImageUrls)
            .sort(([, cardA], [, cardB]) => cardA.companyName.localeCompare(cardB.companyName)),
        [mobileImageUrls]
    );

    // Units and mobile companies share one grid rather than sitting in rows of their own, with
    // the mobile cards directly after the dangerous-waste ones — the order the daily table puts
    // its Mobile row in. Every card is then placed into the first tile it fits, so the second
    // row a dangerous-waste card takes does not leave the tiles beside it half empty.
    const panelCards = useMemo(() => {
        const unitCards = Object.entries(recentImageUrls)
            .filter(([unitId]) => !excludedRecentImageUnitIds.includes(unitId))
            .map(([unitId, image]) => ({
                ...image,
                key: `unit-${unitId}`,
                unitId,
                label: unitId,
                category: image.category || UNCATEGORIZED_CATEGORY
            }))
            .sort((cardA, cardB) => {
                const categoryCompare = cardA.category.localeCompare(cardB.category);
                if (categoryCompare !== 0) return categoryCompare;
                return cardA.unitId.localeCompare(cardB.unitId);
            })
            .map((card) => ({
                ...card,
                rowSpan: card.category === DANGEROUS_WASTE_CATEGORY ? DANGEROUS_WASTE_CARD_ROW_SPAN : 1
            }));

        const mobileCards = mobileImageCards.map(([companyId, card]) => ({
            key: `mobile-${companyId}`,
            companyId,
            label: `${MOBILE_ROW_TITLE} - ${card.companyName}`,
            url: card.url,
            prediction: card.prediction,
            predictionIsRaw: true,
            category: MOBILE_ROW_TITLE,
            timestamp: card.timestamp,
            rowSpan: 1
        }));

        const lastDangerousWasteIndex = unitCards
            .reduce((last, card, index) => (card.category === DANGEROUS_WASTE_CATEGORY ? index : last), -1);
        const mobileInsertIndex = lastDangerousWasteIndex >= 0
            ? lastDangerousWasteIndex + 1
            : unitCards.length;

        return layoutAdminPanelCards([
            ...unitCards.slice(0, mobileInsertIndex),
            ...mobileCards,
            ...unitCards.slice(mobileInsertIndex)
        ]);
    }, [recentImageUrls, excludedRecentImageUnitIds, mobileImageCards]);

    // One panel per company scanning through the mobile app, showing its latest real scan the
    // way each unit's panel shows the unit's own. A company whose recent records are all test
    // ones, or which has captured no image yet, simply has no panel.
    useEffect(() => {
        setMobileImageUrls({});

        // Two companies can carry the same name, so a panel that looks like it should have been
        // held back is traced by id here rather than guessed at from its label.
        if (appRecordCompanies.length > 0) {
            const describe = (companies) => companies
                .map((company) => `${company.name} (${company.id})`)
                .join(", ") || "none";
            console.log(
                "Mobile app panels shown for:",
                describe(mobilePanelCompanies),
                "| held back:",
                describe(appRecordCompanies.filter((company) => !mobilePanelCompanies.includes(company)))
            );
        }

        if (mobilePanelCompanies.length === 0) return undefined;

        const dropCompanyPanel = (companyId) => {
            setMobileImageUrls((prev) => {
                if (!(companyId in prev)) return prev;
                const next = { ...prev };
                delete next[companyId];
                return next;
            });
        };

        const mobileImageUnsubscribers = mobilePanelCompanies.map((company) => onSnapshot(
            query(
                collection(db, COMPANY_APP_RECORDS_COLLECTION, company.id, APP_RECORDS_SUBCOLLECTION),
                orderBy("timestamp", "desc"),
                limit(10)
            ),
            (recordsSnapshot) => {
                const latestRecord = recordsSnapshot.docs
                    .map((recordDoc) => recordDoc.data() || {})
                    .find((recordData) => recordData.is_test !== true);
                const imagePath = latestRecord ? getAppRecordImagePath(latestRecord) : null;

                if (!imagePath) {
                    dropCompanyPanel(company.id);
                    return;
                }

                getDownloadURL(ref(storage, imagePath)).then((url) => {
                    setMobileImageUrls((prev) => ({
                        ...prev,
                        [company.id]: {
                            url,
                            companyName: company.name,
                            prediction: truncateAdminConstituentLabel(
                                getAdminEvalDisplayLabel(latestRecord.eval)
                            ) || "???",
                            timestamp: Number(latestRecord.timestamp)
                        }
                    }));
                }).catch((error) => {
                    console.log(`Error fetching mobile app image for company "${company.id}":`, error);
                    dropCompanyPanel(company.id);
                });
            },
            (error) => {
                console.log(`Error listening to mobile app records for company "${company.id}":`, error);
            }
        ));

        return () => {
            mobileImageUnsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [mobilePanelCompanies, appRecordCompanies]);

    // Only the last seven days are streamed, so the listener stays small no
    // matter how many app records a company has accumulated.
    useEffect(() => {
        setMobileDayCountsByCompany({});
        if (appRecordCompanyIds.length === 0) return undefined;

        const sevenIsoDays = getLastSevenIsoDays();
        const startMs = new Date(`${sevenIsoDays[0]}T00:00:00Z`).getTime();

        const mobileUnsubscribers = appRecordCompanyIds.map((companyId) => onSnapshot(
            query(
                collection(db, COMPANY_APP_RECORDS_COLLECTION, companyId, APP_RECORDS_SUBCOLLECTION),
                where("timestamp", ">=", startMs)
            ),
            (recordsSnapshot) => {
                const dayCounts = {};
                recordsSnapshot.forEach((recordDoc) => {
                    const data = recordDoc.data() || {};
                    if (data.is_test === true) return;
                    const isoDay = getIsoDayFromTimestamp(data.timestamp);
                    if (!isoDay) return;
                    dayCounts[isoDay] = (dayCounts[isoDay] || 0) + 1;
                });
                setMobileDayCountsByCompany((prev) => ({
                    ...prev,
                    [companyId]: dayCounts
                }));
            },
            (error) => {
                console.log(`Error listening to mobile app records for company "${companyId}":`, error);
            }
        ));

        return () => {
            mobileUnsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [appRecordCompanyIds]);

    // Counted server-side so the all-time total never downloads the records.
    // Recomputed whenever the seven-day window changes, which is also when a
    // new app record has arrived.
    useEffect(() => {
        if (appRecordCompanyIds.length === 0) {
            setMobileAllTime(0);
            return undefined;
        }

        let cancelled = false;

        const countAllTimeMobileRecords = async () => {
            try {
                const countsPerCompany = await Promise.all(appRecordCompanyIds.map(async (companyId) => {
                    const recordsCollection = collection(
                        db,
                        COMPANY_APP_RECORDS_COLLECTION,
                        companyId,
                        APP_RECORDS_SUBCOLLECTION
                    );
                    const [allRecordsCount, testRecordsCount] = await Promise.all([
                        getCountFromServer(recordsCollection),
                        getCountFromServer(query(recordsCollection, where("is_test", "==", true)))
                    ]);
                    const allRecords = Number(allRecordsCount.data().count || 0);
                    const testRecords = Number(testRecordsCount.data().count || 0);
                    return Math.max(0, allRecords - testRecords);
                }));

                if (cancelled) return;
                setMobileAllTime(countsPerCompany.reduce((sum, count) => sum + count, 0));
            } catch (error) {
                console.log("Error counting all-time mobile app records:", error);
            }
        };

        countAllTimeMobileRecords();

        return () => {
            cancelled = true;
        };
    }, [appRecordCompanyIds, mobileDayCountsByCompany]);

    useEffect(() => {
        const usageUnsubscribers = [];
        setUsageDayCountsByCategory({});

        usageCategories.forEach((category) => {
            const usageUnsubscribe = onSnapshot(
                collection(db, "admin", "usage-by-day", category),
                (usageSnapshot) => {
                    const dayCounts = {};
                    usageSnapshot.forEach((usageDoc) => {
                        dayCounts[usageDoc.id] = Number(usageDoc.data().count || 0);
                    });
                    setUsageDayCountsByCategory((prev) => ({
                        ...prev,
                        [category]: dayCounts
                    }));
                },
                (error) => {
                    console.log(`Error listening to usage-by-day for category "${category}":`, error);
                }
            );
            usageUnsubscribers.push(usageUnsubscribe);
        });

        return () => {
            usageUnsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [usageCategories]);

    useEffect(() => {
        const sevenIsoDays = getLastSevenIsoDays();
        setWeekIsoDays(sevenIsoDays);

        const rows = usageCategories.map((category) => {
            const dayCounts = usageDayCountsByCategory[category] || {};
            const usage = sevenIsoDays.map((isoDay) => {
                const count = Number(dayCounts[isoDay] || 0);
                return Number.isFinite(count) ? count : 0;
            });

            // The usage-by-day listener holds every day ever recorded for the
            // category, so the all-time total is just the sum of those days.
            // Those day counts are incremented once per record document, so a
            // dangerous-waste sample counts as 1 no matter how many images its
            // back_images array holds. This column counts sets, never images.
            const allTime = Object.values(dayCounts).reduce((sum, dayCount) => {
                const count = Number(dayCount);
                return Number.isFinite(count) ? sum + count : sum;
            }, 0);

            return {
                title: category === "total" ? "Total" : category,
                usage,
                allTime
            };
        }).filter((row) => (
            // "Uncategorized" collects every unit that has no category set, so
            // it is often an empty row. It only earns a place in the table on
            // weeks where those units actually produced samples.
            row.title !== UNCATEGORIZED_CATEGORY
            || row.usage.some((count) => count > 0)
        ));

        rows.sort((a, b) => {
            if (a.title === "Total") return -1;
            if (b.title === "Total") return 1;
            return a.title.localeCompare(b.title);
        });

        const mobileUsage = sevenIsoDays.map((isoDay) => (
            Object.values(mobileDayCountsByCompany).reduce((sum, dayCounts) => {
                const count = Number(dayCounts[isoDay] || 0);
                return Number.isFinite(count) ? sum + count : sum;
            }, 0)
        ));
        const mobileRow = {
            title: MOBILE_ROW_TITLE,
            usage: mobileUsage,
            allTime: mobileAllTime
        };

        // usage-by-day is only written by the unit-record triggers, so samples
        // the mobile app stored under company_app_records never reach the
        // "total" category. Add them here so the Total row stays the sum of
        // the rows below it.
        const totalRow = rows.find((row) => row.title === "Total");
        if (totalRow) {
            totalRow.usage = totalRow.usage.map((count, index) => count + mobileUsage[index]);
            totalRow.allTime += mobileAllTime;
        }

        const dangerousWasteIndex = rows.findIndex((row) => row.title === DANGEROUS_WASTE_CATEGORY);
        if (dangerousWasteIndex >= 0) {
            rows.splice(dangerousWasteIndex + 1, 0, mobileRow);
        } else {
            rows.push(mobileRow);
        }

        setWeekUsage(rows);
    }, [usageCategories, usageDayCountsByCategory, mobileDayCountsByCompany, mobileAllTime]);

    useEffect(() => {
        const sevenIsoDays = getLastSevenIsoDays();
        const rangeStartMs = new Date(`${sevenIsoDays[0]}T00:00:00Z`).getTime();
        const rangeEndMs = new Date(`${sevenIsoDays[6]}T23:59:59.999Z`).getTime();

        const buildOptionFromSeriesMap = (seriesMap) => {
            const dayMs = 24 * 60 * 60 * 1000;
            const chartSeriesLabels = Object.keys(seriesMap).sort((a, b) => {
                return a.localeCompare(b);
            });

            const series = chartSeriesLabels.map((label) => ({
                name: t(label),
                type: "line",
                smooth: false,
                showSymbol: false,
                symbol: 'circle',
                symbolSize: 4,
                emphasis: {
                    focus: 'series',
                    scale: false,
                    itemStyle: {
                        borderWidth: 0,
                        borderColor: 'transparent'
                    }
                },
                lineStyle: {
                    width: 2,
                    color: getCategoryLineColor(label)
                },
                itemStyle: {
                    color: getCategoryLineColor(label),
                    borderWidth: 0
                },
                data: (seriesMap[label] || []).sort((a, b) => a[0] - b[0])
            }));

            setInferenceTrendOption({
                backgroundColor: 'transparent',
                tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                        if (!params || params.length === 0) {
                            return '';
                        }

                        const timestamp = Number(params[0]?.value?.[0]);
                        const formattedTime = Number.isFinite(timestamp)
                            ? new Date(timestamp).toLocaleString()
                            : '';

                        const lines = [formattedTime];
                        params.forEach((param) => {
                            const pointValue = Number(param?.value?.[1]);
                            if (!Number.isFinite(pointValue)) {
                                return;
                            }
                            lines.push(`${param.marker}${param.seriesName}: ${pointValue.toFixed(2)}`);
                        });

                        return lines.join('<br/>');
                    }
                },
                legend: {
                    show: false
                },
                grid: {
                    left: 26,
                    right: 20,
                    top: 20,
                    bottom: 30
                },
                xAxis: {
                    type: 'value',
                    min: rangeStartMs,
                    max: rangeEndMs,
                    interval: dayMs,
                    minInterval: dayMs,
                    maxInterval: dayMs,
                    boundaryGap: false,
                    axisLabel: {
                        color: '#ddd',
                        formatter: (value) => t(getWeekdayLabelFromTimestamp(value))
                    },
                    axisLine: { lineStyle: { color: '#666' } },
                    splitLine: { lineStyle: { color: '#333' } }
                },
                yAxis: {
                    type: 'value',
                    axisLabel: { color: '#ddd' },
                    axisLine: { lineStyle: { color: '#666' } },
                    splitLine: { lineStyle: { color: '#444' } }
                },
                series
            });
        };

        if (USE_MOCK_INFERENCE_TREND_DATA) {
            const mockRecords = [];
            for (let dayOffset = 6; dayOffset >= 0; dayOffset -= 1) {
                const dayStart = new Date(`${sevenIsoDays[6 - dayOffset]}T00:00:00Z`).getTime();
                mockRecords.push(
                    { category: "wood", timestamp: dayStart + (9 * 60 * 60 * 1000), inferenceTime: 1.2 + ((dayOffset % 3) * 0.1) },
                    { category: "wood", timestamp: dayStart + (15 * 60 * 60 * 1000), inferenceTime: 1.35 + ((dayOffset % 2) * 0.12) },
                    { category: "plastic", timestamp: dayStart + (10 * 60 * 60 * 1000), inferenceTime: 1.7 + ((dayOffset % 2) * 0.08) },
                    { category: "plastic", timestamp: dayStart + (16 * 60 * 60 * 1000), inferenceTime: 1.9 + ((dayOffset % 3) * 0.05) },
                    { category: "dangerous_waste", timestamp: dayStart + (12 * 60 * 60 * 1000), inferenceTime: 2.1 + ((dayOffset % 2) * 0.09) },
                    { category: "dangerous_waste", timestamp: dayStart + (18 * 60 * 60 * 1000), inferenceTime: 2.25 + ((dayOffset % 3) * 0.07) }
                );
            }

            const mockSeriesMap = {};
            mockRecords.forEach((record) => {
                if (!mockSeriesMap[record.category]) {
                    mockSeriesMap[record.category] = [];
                }
                const point = [record.timestamp, Number(record.inferenceTime.toFixed(2))];
                mockSeriesMap[record.category].push(point);
            });

            buildOptionFromSeriesMap(mockSeriesMap);
            return;
        }

        const seriesMap = {};
        const excludedSet = new Set(excludedStatsUnitIds);
        const applyRecord = (record, fallbackCategory = "Uncategorized") => {
            if (!record.unitId || excludedSet.has(String(record.unitId))) {
                return;
            }
            const timestamp = Number(record.timestamp);
            const inferenceTime = Number(record.inferenceTime);
            if (!Number.isFinite(timestamp) || !Number.isFinite(inferenceTime)) {
                return;
            }
            if (timestamp < rangeStartMs || timestamp > rangeEndMs) {
                return;
            }

            // Mobile app samples are their own line rather than being folded
            // into dangerous waste: they come either from the "mobile" unit or
            // from an app owner in company_app_records.
            const isMobileRecord = record.unitId === MOBILE_UNIT_ID
                || record.ownerCollectionId === COMPANY_APP_RECORDS_COLLECTION;

            const category = isMobileRecord
                ? MOBILE_ROW_TITLE
                : (unitCategoryById[record.unitId] || fallbackCategory);
            if (!seriesMap[category]) {
                seriesMap[category] = [];
            }
            const point = [timestamp, Number(inferenceTime.toFixed(2))];
            seriesMap[category].push(point);
        };

        recentInferenceRecords.forEach((record) => applyRecord(record, "Uncategorized"));
        recentDangerousWasteRecords.forEach((record) => applyRecord(record, "dangerous_waste"));

        buildOptionFromSeriesMap(seriesMap);
    }, [recentInferenceRecords, recentDangerousWasteRecords, unitCategoryById, usageCategories, excludedStatsUnitIds, weekUsage, t]);

    return (
        <div className="admin-page">
            <div className="group-1">
                <div
                    className="panel-grid"
                    ref={measurePanelGrid}
                    style={panelTileHeight ? { gridAutoRows: `${panelTileHeight}px` } : undefined}
                >
                    {panelCards.map((card) => {
                        const isUnitCard = Boolean(card.unitId);
                        const isClickable = isUnitCard ? Boolean(unitNavigationById[card.unitId]) : true;
                        const openCard = () => {
                            if (isUnitCard) {
                                navigateToUnitDashboard(card.unitId);
                                return;
                            }
                            navigateToCompanyAppRecords(card.companyId);
                        };

                        return (
                            <div
                                key={card.key}
                                className={`image-card ${isClickable ? "clickable" : ""} ${card.isLastColumn ? "popup-left" : ""}`}
                                style={{
                                    borderRadius: card.borderRadius,
                                    gridColumn: card.column + 1,
                                    gridRow: `${card.row + 1} / span ${card.rowSpan}`
                                }}
                                onClick={openCard}
                                role={isClickable ? "button" : undefined}
                                tabIndex={isClickable ? 0 : undefined}
                                onKeyDown={(event) => {
                                    if (!isClickable) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openCard();
                                    }
                                }}
                            >
                                {isUnitCard && (
                                    <div className="image-hover-info">
                                        <div className="hover-row">
                                            <div className="hover-label">System:</div>
                                            <div className="hover-value">{card.systemVersion || "-"}</div>
                                        </div>
                                        <div className="hover-row">
                                            <div className="hover-label">Model{(card.modelPaths || []).length > 1 ? "s" : ""}:</div>
                                            <div className="hover-value">
                                                {(card.modelPaths || []).length > 0 ? (
                                                    card.modelPaths.map((path, modelIndex) => (
                                                        <div key={`${card.key}-model-${modelIndex}`} className="hover-model-item">
                                                            {path}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="hover-model-item">-</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="image-media">
                                    <img
                                        src={card.url}
                                        alt={isUnitCard
                                            ? `Recent record for unit ${card.unitId}`
                                            : `Latest mobile app scan for ${card.label}`}
                                        onLoad={PANEL_TILE_REFERENCE_CATEGORIES.includes(card.category)
                                            ? captureReferenceAspectRatio
                                            : undefined}
                                    />
                                    {card.timestamp ? (
                                        <div className="image-date-badge">
                                            {isUnitCard && (
                                                <span
                                                    className="image-status-led"
                                                    style={{ backgroundColor: getStatusLedColor(card.unitId) }}
                                                ></span>
                                            )}
                                            {formatDateWithoutYear(card.timestamp)}
                                        </div>
                                    ) : null}
                                    <div className="floating-info" style={{ backgroundColor: getCategoryOverlayColor(card.category) }}>
                                        <div className="unit-id" style={{ color: getCategoryUnitTextColor(card.category) }}>
                                            {card.label}
                                        </div>
                                        <div className="prediction">
                                            {card.predictionIsRaw ? card.prediction : t(card.prediction)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {Object.entries(screenMonitoringUrls).some(([unitId]) => !excludedRecentImageUnitIds.includes(unitId)) && (
                    <div className="screen-monitoring-panel">
                        <div className="screen-monitoring-grid">
                            {Object.entries(screenMonitoringUrls)
                                .filter(([unitId]) => !excludedRecentImageUnitIds.includes(unitId))
                                .sort(([unitIdA], [unitIdB]) => {
                                    const categoryA = unitCategoryById[unitIdA] || "Uncategorized";
                                    const categoryB = unitCategoryById[unitIdB] || "Uncategorized";
                                    const categoryCompare = categoryA.localeCompare(categoryB);
                                    if (categoryCompare !== 0) return categoryCompare;
                                    return unitIdA.localeCompare(unitIdB);
                                })
                                .map(([unitId, url]) => (
                                    <div
                                        key={unitId}
                                        className={`screen-monitoring-thumb ${unitNavigationById[unitId] ? "clickable" : ""}`}
                                        onClick={() => navigateToUnitDashboard(unitId)}
                                        role={unitNavigationById[unitId] ? "button" : undefined}
                                        tabIndex={unitNavigationById[unitId] ? 0 : undefined}
                                        onKeyDown={(event) => {
                                            if (!unitNavigationById[unitId]) return;
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                navigateToUnitDashboard(unitId);
                                            }
                                        }}
                                    >
                                        <div className="screen-monitoring-frame">
                                            <img
                                                src={url}
                                                alt=""
                                                loading="lazy"
                                            />
                                        </div>
                                        <span
                                            className="screen-monitoring-unit-id"
                                            style={{ color: getCategoryLineColor(unitCategoryById[unitId] || "Uncategorized") }}
                                            title={unitId}
                                        >
                                            {unitId}
                                        </span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="group-2">
                <div
                    className="row weekly-usage-clickable"
                    onClick={() => navigate('/admin/weekly-usage')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate('/admin/weekly-usage');
                        }
                    }}
                >
                    {/* Week view of combined usage */}
                    <div className="week-day-labels">
                        {
                            weekIsoDays.map((isoDay, index) => (
                                <div key={index} className="week-day-label">
                                    {t(getWeekdayLabelFromIsoDay(isoDay))}
                                </div>
                            ))
                        }
                        <div className="week-all-time-label">{t("All Time")}</div>
                        <div className="week-day-separators">
                            {
                                Array.from({ length: 6 }, (_, separatorIndex) => (
                                    <div
                                        key={separatorIndex}
                                        className="week-day-separator"
                                        style={{ left: `calc((100% - var(--week-all-time-track)) * ${separatorIndex + 1} / 7 - 0.5px)` }}
                                    ></div>
                                ))
                            }
                            <div className="week-all-time-separator"></div>
                        </div>
                    </div>
                    <div className="week-rows">
                        {
                            weekUsage.map((weekUsageObject, rowIndex) => (
                                <div key={rowIndex} className="week-row">
                                    <div className="row-label">
                                        {
                                            <div className="row-label-text" style={{ color: getBaseColor(rowIndex) }}>
                                                {t(weekUsageObject.title)}
                                            </div>
                                        }
                                        {rowIndex < weekUsage.length - 1 && (
                                            <div className="row-label-separator"></div>
                                        )}
                                    </div>
                                    <div className="week-values">
                                        {
                                            weekUsageObject.usage.map((value, index) => (
                                                <div key={index} className="week-cell" style={{ background: getShadeOfDayValue(value, rowIndex) }}>
                                                    {value}
                                                </div>
                                            ))
                                        }
                                        <div
                                            className="week-cell week-all-time-cell"
                                            style={{
                                                background: getColorWithOpacity(getBaseColor(rowIndex), 0.28),
                                                borderColor: getColorWithOpacity(getBaseColor(rowIndex), 0.65)
                                            }}
                                        >
                                            {formatAllTimeCount(weekUsageObject.allTime)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
                <div className="inference-times-row">
                    <div
                        className="inference-time-trend-chart clickable"
                        onClick={() => navigate('/admin/inference-times')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                navigate('/admin/inference-times');
                            }
                        }}
                    >
                        <div className="inference-time-unit">{t("Inference time (s)")}</div>
                        {inferenceTrendOption && (
                            <ReactECharts
                                option={inferenceTrendOption}
                                notMerge={true}
                                style={{ width: '100%', height: '260px' }}
                            />
                        )}
                    </div>
                </div>
                <div className="system-info-panel">
                    <table className="system-info-table">
                        <thead>
                            <tr>
                                <th>{t("Unit")}</th>
                                <th>{t("System version")}</th>
                                <th>{t("Model")}</th>
                                <th>{t("Memory use")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(recentImageUrls)
                                .filter(([unitId]) => !excludedRecentImageUnitIds.includes(unitId))
                                .sort(([unitIdA, imageA], [unitIdB, imageB]) => {
                                    const categoryA = imageA.category || "Uncategorized";
                                    const categoryB = imageB.category || "Uncategorized";
                                    const categoryCompare = categoryA.localeCompare(categoryB);
                                    if (categoryCompare !== 0) return categoryCompare;
                                    return unitIdA.localeCompare(unitIdB);
                                })
                                .map(([unitId, { systemVersion, modelPaths, memoryUse, category }]) => (
                                    <tr key={unitId}>
                                        <td style={{ color: getCategoryLineColor(category) }}>{unitId}</td>
                                        <td>{systemVersion || "-"}</td>
                                        <td>{(modelPaths || []).length > 0 ? modelPaths.map(shortenModelPath).join(", ") : "-"}</td>
                                        <td>{memoryUse != null ? `${memoryUse}%` : "-"}</td>
                                    </tr>
                                ))
                            }
                            <tr key={MOBILE_UNIT_DEFAULTS.unitId}>
                                <td style={{ color: getCategoryLineColor(MOBILE_ROW_TITLE) }}>
                                    {MOBILE_UNIT_DEFAULTS.unitId}
                                </td>
                                <td>{MOBILE_UNIT_DEFAULTS.systemVersion}</td>
                                <td>{MOBILE_UNIT_DEFAULTS.model}</td>
                                <td>{MOBILE_UNIT_DEFAULTS.memoryUse != null ? `${MOBILE_UNIT_DEFAULTS.memoryUse}%` : "-"}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AdminPage;
